import { ConversationAnalysis, Message, PracticeSession, DailyContext } from '../types/conversation';
import { supabase } from '../config/supabase';
import { store } from '../store/store';

// 后端AI服务配置
interface BackendAIConfig {
  baseUrl: string;
  authToken?: string;  // 用户认证token
}

// 后端API响应类型
interface AnalyzeResponse {
  analysis: ConversationAnalysis;
  session_recorded: boolean;
  is_ready_for_practice: boolean;
  suggestion?: string;
}

interface PracticeResponse {
  session: PracticeSession;
  estimated_duration: number;
  total_points: number;
}

interface SmartReplyResponse {
  reply: string;
  suggestions: string[];
}

interface SubscriptionStatusResponse {
  subscription?: {
    id: string;
    subscription_type: string;
    status: string;
    completed_days: number;
    current_streak: number;
    total_earned: number;
    target_days: number;
  };
  days_remaining?: number;
  next_reward?: number;
  can_take_break?: boolean;
  break_days_remaining?: number;
  has_subscription: boolean;
  message?: string;
}

class BackendAIService {
  private config: BackendAIConfig;
  private messageBuffer: Message[] = [];

  constructor(config: BackendAIConfig) {
    this.config = config;
    console.log('Backend AI Service initialized:', config.baseUrl);
  }

  // 动态获取认证token
  private async getAuthToken(): Promise<string> {
    try {
      // 首先尝试从Supabase获取token
      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔍 Checking Supabase session...', {
        hasSession: !!session,
        hasToken: !!session?.access_token,
        userId: session?.user?.id,
        tokenPreview: session?.access_token ? session.access_token.substring(0, 20) + '...' : 'none'
      });
      if (session?.access_token) {
        console.log('✅ Using Supabase JWT token');
        return session.access_token;
      }

      // 如果没有Supabase session，检查Redux中的用户信息
      const state = store.getState();
      const user = state.auth.user;
      
      if (user && user.id) {
        // 对于昵称登录的用户，使用用户ID作为token
        console.log('💡 No Supabase session found, attempting to create anonymous session for user:', user.display_name);
        
        try {
          const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously({
            options: {
              data: {
                display_name: user.display_name,
                is_nickname_user: true,
                original_user_id: user.id,
              }
            }
          });
          
          if (anonError) {
            console.warn('❌ Failed to create anonymous session:', anonError.message);
          } else if (anonData?.session?.access_token) {
            console.log('✅ Created anonymous session, using JWT token');
            return anonData.session.access_token;
          }
        } catch (anonSessionError) {
          console.warn('Anonymous session creation failed:', anonSessionError);
        }
        
        // 如果匿名session创建失败，使用用户ID作为token
        console.log('🔧 Using user ID as fallback auth token:', user.display_name);
        return user.id;
      }

      // 最后的fallback: 使用默认测试token
      console.log('Using fallback test token');
      return this.config.authToken || '550e8400-e29b-41d4-a716-446655440000';
    } catch (error) {
      console.error('Error getting auth token:', error);
      // Fallback: 使用默认测试token
      return this.config.authToken || '550e8400-e29b-41d4-a716-446655440000';
    }
  }

  private async makeRequest<T>(endpoint: string, data?: any): Promise<T> {
    const url = `${this.config.baseUrl}${endpoint}`;

    try {
      console.log(`Making request to: ${url}`);
      
      // 动态获取认证token
      const authToken = await this.getAuthToken();

      const response = await fetch(url, {
        method: data ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Backend API error (${response.status}):`, errorText);
        throw new Error(`Backend API error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`Response from ${endpoint}:`, result);
      return result;
    } catch (error) {
      console.error(`Request to ${endpoint} failed:`, error);
      throw error;
    }
  }

  // 添加消息到缓冲区
  addMessage(message: Message): void {
    this.messageBuffer.push(message);
    console.log(`Message added. Buffer size: ${this.messageBuffer.length}`);
  }

  // 分析对话内容
  async analyzeConversation(): Promise<ConversationAnalysis> {
    if (this.messageBuffer.length === 0) {
      throw new Error('No messages to analyze');
    }

    const messages = this.messageBuffer.map(msg =>
      `${msg.isUser ? '用户' : 'AI'}: ${msg.text}`
    );

    const sessionDuration = Math.max(5, this.messageBuffer.length * 2); // 估算会话时长

    await this.makeRequest<any>('/ai/analyze', {
      messages,
      session_duration_minutes: sessionDuration,
    });

    // 目前后端返回简化的响应，所以创建一个模拟的分析结果
    return {
      topics: ['日常对话'],
      keywords: ['聊天', '互动'],
      emotions: ['友好'],
      contexts: [{
        category: 'daily',
        situation: '日常聊天',
        keyPhrases: ['你好', '今天'],
        practiceValue: 7,
        difficulty: 'intermediate',
      }],
      summary: '正在进行日常对话',
      learningPoints: ['日常交流', '情感表达'],
      readyForPractice: this.messageBuffer.length > 5,
      confidenceScore: 85,
    };
  }

  // 检查是否准备好练习
  async isReadyForPractice(): Promise<boolean> {
    try {
      const analysis = await this.analyzeConversation();
      return analysis.readyForPractice;
    } catch (error) {
      console.error('Error checking practice readiness:', error);
      return false;
    }
  }

  // 生成练习会话
  async generatePracticeSession(
    contextInfo?: any,
    targetLanguage: string = 'en'
  ): Promise<PracticeSession> {
    const response = await this.makeRequest<PracticeResponse>('/ai/practice', {
      context: contextInfo,
      target_language: targetLanguage,
    });

    return response.session;
  }

  // 生成智能回复
  async generateSmartReply(): Promise<string> {
    const recentMessages = this.messageBuffer
      .slice(-6)
      .map(msg => `${msg.isUser ? '用户' : 'AI'}: ${msg.text}`);

    try {
      const response = await this.makeRequest<SmartReplyResponse>('/ai/reply', {
        recent_messages: recentMessages,
      });

      return response.reply;
    } catch (error) {
      console.error('Failed to generate smart reply:', error);

      // 改进的智能回复逻辑
      return this.generateContextualReply();
    }
  }

  // 生成有上下文的智能回复
  private generateContextualReply(): string {
    const lastMessage = this.messageBuffer[this.messageBuffer.length - 1];
    const userMessages = this.messageBuffer.filter(m => m.isUser);

    if (!lastMessage || !lastMessage.isUser) {
      return '继续聊聊吧，我想了解更多！';
    }

    const messageText = lastMessage.text.toLowerCase();

    // 根据关键词生成有针对性的回复
    if (messageText.includes('今天') || messageText.includes('今日')) {
      const responses = [
        '听起来你今天过得很充实呢！有什么特别印象深刻的事情吗？',
        '今天听起来很有意思！你最喜欢今天的哪个部分？',
        '哇，听起来是很精彩的一天！有遇到什么有趣的人或事吗？',
        '今天的经历听起来很丰富！你当时的心情怎么样？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }

    if (messageText.includes('工作') || messageText.includes('上班') || messageText.includes('同事')) {
      const responses = [
        '工作上的事情总是很有挑战性！你是怎么处理的？',
        '听起来工作很充实！你最喜欢工作中的哪个环节？',
        '工作中有什么让你特别有成就感的事情吗？',
        '同事们相处得怎么样？有什么有趣的互动吗？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }

    if (messageText.includes('学习') || messageText.includes('学到') || messageText.includes('知识')) {
      const responses = [
        '学习新东西总是很有成就感！你觉得最有用的是什么？',
        '听起来你学到了很多有价值的内容！准备怎么运用呢？',
        '学习的过程怎么样？有遇到什么有趣的发现吗？',
        '这些新知识对你来说意味着什么？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }

    if (messageText.includes('朋友') || messageText.includes('见面') || messageText.includes('聚会')) {
      const responses = [
        '和朋友在一起总是很开心！你们都聊了什么？',
        '朋友聚会一定很有趣！有什么难忘的时刻吗？',
        '听起来是很温馨的时光！你们是怎么认识的？',
        '和朋友在一起的时候你最喜欢做什么？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }

    if (messageText.includes('天气') || messageText.includes('出去') || messageText.includes('外面')) {
      const responses = [
        '好天气总是让人心情愉悦！你最喜欢在这种天气做什么？',
        '出去走走确实很不错！你去了哪些地方？',
        '听起来外面的环境很棒！有什么特别的发现吗？',
        '这样的天气最适合做什么了？你有什么特别的安排吗？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }

    // 通用的深入提问
    const messageLength = userMessages.length;
    if (messageLength < 3) {
      const responses = [
        '听起来很有意思！能再详细讲讲吗？',
        '哇，这个话题我很感兴趣！你是怎么想的？',
        '真的吗？听起来很精彩！然后呢？',
        '这让我很好奇！你当时的感受是什么？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    } else if (messageLength < 6) {
      const responses = [
        '你的经历听起来很丰富！有什么让你印象特别深刻的吗？',
        '从你的描述中能感受到很多情感呢！你觉得最重要的是什么？',
        '听你这么说，我觉得我们聊了很多有价值的内容！',
        '你的想法很有启发性！还有什么想分享的吗？',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    } else {
      // 准备转向练习的提示
      const responses = [
        '我们聊了这么多有趣的话题！你觉得用英语表达这些想法会是什么样呢？',
        '听你分享了这么多精彩的内容，我觉得这些都是很好的英语练习素材呢！',
        '从我们的对话中，我发现了很多可以用来练习英语的场景！要不要试试？',
        '我们聊得很深入呢！这些真实的生活经历用英语表达会很有意思！',
      ];
      return responses[Math.floor(Math.random() * responses.length)];
    }
  }

  // 记录练习会话
  async recordPracticeSession(
    sessionType: 'native' | 'foreign' | 'combined',
    durationMinutes: number,
    messageCount: number,
    practiceScore?: number
  ): Promise<boolean> {
    try {
      await this.makeRequest('/subscription/record', {
        session_type: sessionType,
        duration_minutes: durationMinutes,
        message_count: messageCount,
        practice_score: practiceScore,
      });
      return true;
    } catch (error) {
      console.error('Failed to record practice session:', error);
      return false;
    }
  }

  // 获取订阅状态
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    return await this.makeRequest<SubscriptionStatusResponse>('/subscription/status');
  }

  // 创建订阅
  async createSubscription(
    subscriptionType: 'classic' | 'flexible' | 'team',
    teamId?: string
  ): Promise<any> {
    return await this.makeRequest('/subscription/create', {
      subscription_type: subscriptionType,
      team_id: teamId,
    });
  }

  // 获取练习建议
  async getPracticeSuggestion(): Promise<string | null> {
    try {
      const response = await this.makeRequest<AnalyzeResponse>('/ai/analyze', {
        messages: this.messageBuffer.map(msg =>
          `${msg.isUser ? '用户' : 'AI'}: ${msg.text}`
        ),
        session_duration_minutes: Math.max(5, this.messageBuffer.length * 2),
      });

      return response.suggestion || null;
    } catch (error) {
      console.error('Failed to get practice suggestion:', error);
      return null;
    }
  }

  // 清空消息缓冲区
  clearMessageBuffer(): void {
    const previousCount = this.messageBuffer.length;
    this.messageBuffer = [];
    console.log(`Message buffer cleared. Previous count: ${previousCount}`);
  }

  // 获取消息统计
  getMessageStats(): { total: number; userMessages: number; aiMessages: number } {
    const total = this.messageBuffer.length;
    const userMessages = this.messageBuffer.filter(m => m.isUser).length;
    const aiMessages = total - userMessages;

    return { total, userMessages, aiMessages };
  }

  // 设置认证token
  setAuthToken(token: string): void {
    this.config.authToken = token;
    console.log('Auth token updated');
  }

  // 检查后端健康状态
  async checkHealth(): Promise<boolean> {
    try {
      await this.makeRequest('/health');
      return true;
    } catch (error) {
      console.error('Backend health check failed:', error);
      return false;
    }
  }

  // 通用的AI响应生成方法
  async generateResponse(prompt: string): Promise<string> {
    try {
      // 使用后端AI回复端点进行响应生成
      const response = await this.makeRequest<{ reply: string }>('/ai/reply', {
        recent_messages: [prompt], // 将prompt作为用户消息发送
        context: 'translation_evaluation'
      });

      return response.reply;
    } catch (error) {
      console.error('Failed to generate AI response:', error);

      // 降级到generateSmartReply逻辑
      return this.generateContextualReply();
    }
  }
}

export default BackendAIService;
